import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Constants } from 'src/app/app.constants';
import { Guide, Location } from 'src/app/models';
import { AssetService } from 'src/app/services/asset.service';
import { LocationService } from 'src/app/services';
import { saveAs } from 'file-saver';
import { SecurityContext } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FullScreenImageViewerComponent } from '../full-screen-image-viewer/full-screen-image-viewer.component';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { AnalyticsAbstract } from 'src/app/services/analytics/analytics.abstract';

// Enum for image sizes
export enum ImageSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

@Component({
  selector: 'asset-viewer',
  templateUrl: './asset-viewer.component.html',
  styleUrls: ['./asset-viewer.component.css'],
})
export class AssetViewerComponent implements OnInit {
  loading = true;
  imageUrl: string | SafeUrl = '';
  originalUrl: string = '';
  mockupUrl: string | SafeUrl = '';

  // Location picker properties
  locations: Location[] = [];
  locationsLoading = false;
  locationFormControl = new FormControl();
  hasMultipleLocations = false;
  selectedLocation: Location | null = null;

  imageSizes: { value: ImageSize; label: string; description: string }[] = [
    {
      value: ImageSize.Small,
      label: 'Small (A5)',
      description: '1,410px x 2,000px',
    },
    {
      value: ImageSize.Medium,
      label: 'Medium (A4)',
      description: '2,467px x 3,500px',
    },
    {
      value: ImageSize.Large,
      label: 'Large (A1)',
      description: '4,406px x 6,250px',
    },
  ];
  sizeFormControl = new FormControl();
  selectedSize: ImageSize = ImageSize.Small;
  hasMultipleSizes = false;
  hasMockup = false;

  constructor(
    public constants: Constants,
    @Inject(MAT_DIALOG_DATA) public data: { guide: Guide },
    private sanitizer: DomSanitizer,
    public matDialogRef: MatDialogRef<AssetViewerComponent>,
    private assetService: AssetService,
    private locationService: LocationService,
    private dialog: MatDialog,
    private mixpanel: AnalyticsAbstract,
  ) {
    this.initializeImageUrls();

    this.selectedSize = ImageSize.Small;

    this.sizeFormControl.setValue(this.imageSizes[0]);

    this.sizeFormControl.valueChanges.subscribe((size) => {
      if (size !== null) {
        this.onSizeChange(size);
      }
    });
  }

  private initializeImageUrls(): void {
    const guide = this.data.guide;

    // Check if we have the key and extension fields (newest structure)
    if (guide.key && guide.extension) {
      // Construct the image URLs
      const imageUrls = this.constructImageUrls(guide.key, guide.extension);

      // Set the original URL to the small size
      this.originalUrl = imageUrls.small;
      this.imageUrl = this.originalUrl;

      // Multiple sizes are always available with this structure
      this.hasMultipleSizes = true;

      // Mockups are always available with this structure
      this.hasMockup = true;

      // Set the mockup URL based on the selected size
      this.mockupUrl = imageUrls.mockupSmall;
    }
    // Check if we have the images object structure (previous structure)
    else if (guide.images) {
      this.originalUrl = guide.images.small || '';
      this.imageUrl = this.originalUrl;

      this.hasMultipleSizes = !!(guide.images.medium || guide.images.large);

      if (guide.mockups) {
        this.hasMockup = true;
        this.updateMockupUrl(this.selectedSize);
      }
    } else {
      this.originalUrl = guide.imageUrl || '';
      this.imageUrl = this.originalUrl;

      this.hasMultipleSizes = !!(guide.imageMediumUrl || guide.imageLargeUrl);
    }
  }

  /**
   * Constructs image and mockup URLs based on key and extension
   * @param key The asset key (e.g., "coffee_habit")
   * @param extension The file extension (e.g., "png")
   * @returns Object containing all image and mockup URLs
   */
  private constructImageUrls(
    key: string,
    extension: string,
  ): {
    thumbnail: string;
    small: string;
    medium: string;
    large: string;
    mockupSmall: string;
    mockupMedium: string;
    mockupLarge: string;
  } {
    const baseUrl = `https://remy-system.s3.eu-west-2.amazonaws.com/assets/${key}`;

    return {
      thumbnail: `${baseUrl}/${key}_sm.${extension}`,
      small: `${baseUrl}/${key}_a5.${extension}`,
      medium: `${baseUrl}/${key}_a4.${extension}`,
      large: `${baseUrl}/${key}_a1.${extension}`,
      mockupSmall: `${baseUrl}/${key}_mockup_a5.${extension}`,
      mockupMedium: `${baseUrl}/${key}_mockup_a4.${extension}`,
      mockupLarge: `${baseUrl}/${key}_mockup_a1.${extension}`,
    };
  }

  private updateMockupUrl(size: ImageSize): void {
    const guide = this.data.guide;

    // Check if we have the key and extension fields (newest structure)
    if (guide.key && guide.extension) {
      const imageUrls = this.constructImageUrls(guide.key, guide.extension);

      switch (size) {
        case ImageSize.Small:
          this.mockupUrl = imageUrls.mockupSmall;
          break;
        case ImageSize.Medium:
          this.mockupUrl = imageUrls.mockupMedium;
          break;
        case ImageSize.Large:
          this.mockupUrl = imageUrls.mockupLarge;
          break;
      }
    }
    // Check if we have the mockups object (previous structure)
    else if (guide.mockups) {
      switch (size) {
        case ImageSize.Small:
          this.mockupUrl = guide.mockups.small || '';
          break;
        case ImageSize.Medium:
          this.mockupUrl = guide.mockups.medium || guide.mockups.small || '';
          break;
        case ImageSize.Large:
          this.mockupUrl =
            guide.mockups.large ||
            guide.mockups.medium ||
            guide.mockups.small ||
            '';
          break;
      }
    }
  }

  ngOnInit() {
    this.getLocations();
  }

  getLocations(): void {
    this.locationsLoading = true;

    this.locationService.getLocations(1000, 0).subscribe({
      next: (res: any) => {
        if (res) {
          this.locations = res.docs.map((el: Location) => {
            return {
              ...el,
              formattedAddress: this.constants.constructAddress(el.address!),
            };
          });

          this.hasMultipleLocations = this.locations.length > 1;

          if (this.locations.length > 0) {
            this.selectedLocation = this.locations[0];
            this.locationFormControl.setValue(this.locations[0]);
          }

          this.loadImageWithLogo(this.selectedLocation?._id);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.constants.snack('Error loading locations');
      },
      complete: () => {
        this.locationsLoading = false;
      },
    });
  }

  onLocationChange(location: Location): void {
    this.selectedLocation = location;

    this.mixpanel.track(Constants.analytics_keys.selectAssetLocation, {
      Asset: this.data.guide.title,
      Location: location.name,
    });

    if (location && location._id) {
      this.loadImageWithLogo(location._id);
    } else {
      this.loadImageWithLogo();
    }
  }

  onSizeChange(size: ImageSize): void {
    this.selectedSize = size;

    this.mixpanel.track(Constants.analytics_keys.selectAssetSize, {
      Asset: this.data.guide.title,
      Size: size,
    });

    this.updateImageUrlForSize(size);

    if (this.hasMockup) {
      this.updateMockupUrl(size);
    }
  }

  getSizeLabel(): string {
    switch (this.selectedSize) {
      case ImageSize.Small:
        return 'A5';
      case ImageSize.Medium:
        return 'A4';
      case ImageSize.Large:
        return 'A1';
      default:
        return 'A5';
    }
  }

  updateImageUrlForSize(size: ImageSize): void {
    const currentUrl = this.originalUrl;
    const guide = this.data.guide;

    // Check if we have the key and extension fields (newest structure)
    if (guide.key && guide.extension) {
      const imageUrls = this.constructImageUrls(guide.key, guide.extension);

      switch (size) {
        case ImageSize.Small:
          this.originalUrl = imageUrls.small;
          break;
        case ImageSize.Medium:
          this.originalUrl = imageUrls.medium;
          break;
        case ImageSize.Large:
          this.originalUrl = imageUrls.large;
          break;
      }
    }
    // Check if we have the images object (previous structure)
    else if (guide.images) {
      switch (size) {
        case ImageSize.Small:
          this.originalUrl = guide.images.small || '';
          break;
        case ImageSize.Medium:
          this.originalUrl = guide.images.medium || guide.images.small || '';
          break;
        case ImageSize.Large:
          this.originalUrl =
            guide.images.large ||
            guide.images.medium ||
            guide.images.small ||
            '';
          break;
      }
    } else {
      switch (size) {
        case ImageSize.Small:
          this.originalUrl = guide.imageUrl || '';
          break;
        case ImageSize.Medium:
          this.originalUrl = guide.imageMediumUrl || guide.imageUrl || '';
          break;
        case ImageSize.Large:
          this.originalUrl =
            guide.imageLargeUrl || guide.imageMediumUrl || guide.imageUrl || '';
          break;
      }
    }

    if (currentUrl !== this.originalUrl) {
      this.loadImageWithLogo(this.selectedLocation?._id);
    }
  }

  loadImageWithLogo(locationId?: string) {
    this.loading = true;

    if (!this.originalUrl) {
      this.loading = false;
      return;
    }

    const img = new Image();
    img.onload = () => {
      this.assetService
        .generateWithLogo(this.originalUrl, locationId)
        .subscribe({
          next: (response: any) => {
            if (response && response.imageUrl) {
              if (response.imageUrl.startsWith('data:')) {
                this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(
                  response.imageUrl,
                );
              } else {
                this.imageUrl = response.imageUrl;
              }
            }
          },
          error: (error) => {
            this.imageUrl = this.originalUrl;
          },
          complete: () => {
            this.loading = false;
          },
        });
    };

    img.onerror = () => {
      this.imageUrl = this.originalUrl;
      this.loading = false;
    };

    img.src = this.originalUrl;
  }

  openFullScreenImage(imageUrl: string | SafeUrl, title: string): void {
    this.mixpanel.track(Constants.analytics_keys.viewAssetFullScreen, {
      Asset: this.data.guide.title,
      Image: title,
    });

    this.dialog.open(FullScreenImageViewerComponent, {
      data: {
        imageUrl: imageUrl,
        title: title,
      },
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'full-screen-dialog',
      autoFocus: false,
      scrollStrategy: new NoopScrollStrategy(),
    });
  }

  downloadImage() {
    let fileName = 'asset.png';

    this.mixpanel.track(Constants.analytics_keys.downloadAsset, {
      Asset: this.data.guide.title,
      Size: this.selectedSize,
      Location: this.selectedLocation?.name,
    });

    try {
      if (this.data.guide.title) {
        const safeTitle = this.data.guide.title
          .replace(/[^a-z0-9]/gi, '_')
          .toLowerCase();

        fileName = safeTitle + '.png';
      } else if (this.originalUrl) {
        const urlParts = this.originalUrl.split('/');
        const lastPart = urlParts[urlParts.length - 1];
        if (lastPart && lastPart.includes('.')) {
          fileName = lastPart;
        }
      }

      try {
        let imageToDownload: string;

        if (typeof this.imageUrl === 'string') {
          imageToDownload = this.imageUrl;
        } else {
          imageToDownload =
            this.sanitizer.sanitize(SecurityContext.URL, this.imageUrl) || '';
        }

        if (imageToDownload.startsWith('data:')) {
          fileName = this.addSizeAndLocationToFilename(fileName);

          this.downloadBase64Image(imageToDownload, fileName);
        } else {
          this.processAndDownloadImage(fileName);
        }
      } catch (error) {
        this.processAndDownloadImage(fileName);
      }
    } catch (error) {
      this.constants.snack('Error downloading image. Please try again.');
    }
  }

  private addSizeAndLocationToFilename(fileName: string): string {
    const sizeLabel =
      this.selectedSize === ImageSize.Small
        ? 'A5'
        : this.selectedSize === ImageSize.Medium
          ? 'A4'
          : 'A1';

    let locationName = '';
    if (this.selectedLocation && this.selectedLocation.name) {
      locationName = this.selectedLocation.name
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();
    }

    if (fileName.includes('.')) {
      const parts = fileName.split('.');
      return `${parts[0]}-${sizeLabel}${
        locationName ? '-' + locationName : ''
      }.${parts[1]}`;
    } else {
      return `${fileName}-${sizeLabel}${
        locationName ? '-' + locationName : ''
      }.png`;
    }
  }

  private downloadBase64Image(base64Data: string, fileName: string) {
    try {
      const base64 = base64Data.split(',')[1];
      const mimeType = base64Data.split(',')[0].split(':')[1].split(';')[0];
      const byteCharacters = atob(base64);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      const blob = new Blob(byteArrays, { type: mimeType });
      saveAs(blob, fileName);
    } catch (error) {
      this.constants.snack('Error downloading image. Please try again.');
    }
  }

  private processAndDownloadImage(fileName: string) {
    this.loading = true;

    const locationId = this.selectedLocation?._id;

    let urlToProcess = this.originalUrl;
    const guide = this.data.guide;

    // Check if we have the key and extension fields (newest structure)
    if (guide.key && guide.extension) {
      const imageUrls = this.constructImageUrls(guide.key, guide.extension);

      switch (this.selectedSize) {
        case ImageSize.Small:
          urlToProcess = imageUrls.small;
          break;
        case ImageSize.Medium:
          urlToProcess = imageUrls.medium;
          break;
        case ImageSize.Large:
          urlToProcess = imageUrls.large;
          break;
      }
    }
    // Check if we have the images object (previous structure)
    else if (guide.images) {
      switch (this.selectedSize) {
        case ImageSize.Small:
          urlToProcess = guide.images.small || '';
          break;
        case ImageSize.Medium:
          urlToProcess = guide.images.medium || guide.images.small || '';
          break;
        case ImageSize.Large:
          urlToProcess =
            guide.images.large ||
            guide.images.medium ||
            guide.images.small ||
            '';
          break;
      }
    } else {
      switch (this.selectedSize) {
        case ImageSize.Small:
          urlToProcess = guide.imageUrl || '';
          break;
        case ImageSize.Medium:
          urlToProcess = guide.imageMediumUrl || guide.imageUrl || '';
          break;
        case ImageSize.Large:
          urlToProcess =
            guide.imageLargeUrl || guide.imageMediumUrl || guide.imageUrl || '';
          break;
      }
    }

    fileName = this.addSizeAndLocationToFilename(fileName);

    this.assetService.generateWithLogo(urlToProcess, locationId).subscribe({
      next: (response: any) => {
        if (response && response.imageUrl) {
          this.downloadBase64Image(response.imageUrl, fileName);
        } else {
          this.constants.snack('Error processing image. Please try again.');
        }
      },
      error: (error) => {
        this.constants.snack('Error processing image. Please try again.');
      },
      complete: () => {
        this.loading = false;
      },
    });
  }
}
